import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useTranslate } from "helpers";
import { getYoutubeEmbedUrl, getYoutubeThumbnailUrl } from "helpers/youtube";
import { VehicleGalleryPreviewProps, VehicleGalleryPreviewRef } from "./types";
import { PictureGalleryPreview } from "ui/components/Pictures";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { deleteVehiclePictureAPI, getFleetVehiclePicturesAPI, reorderVehiclePicturesAPI } from "services/api/fleet/vehicles/pictures/api";
import { FleetVehiclePictureRow } from "services/api/fleet/vehicles/pictures/types";
import { initialDataWithPagination } from "services/api/consts";
import { showNotification } from "services/api/snackbar";

const VehicleGalleryPreview = forwardRef<VehicleGalleryPreviewRef, VehicleGalleryPreviewProps>((props, ref) => {
    const { fleetVehicleDetail, onPictureDeleted, videoUrl } = props;
    const { t } = useTranslate();
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedPictureId, setSelectedPictureId] = useState<string | null>(null);
    const [videoOpen, setVideoOpen] = useState(false);
    const [fetchedPictures, setFetchedPictures] = useState<FleetVehiclePictureRow[]>([]);

    // Video ulazi u galeriju samo ako je link prepoznat — neprepoznat bi dao pločicu
    // sa slomljenom slikom.
    const video = useMemo(() => {
        if (!videoUrl?.trim()) return null;

        const thumbnailUrl = getYoutubeThumbnailUrl(videoUrl);
        const embedUrl = getYoutubeEmbedUrl(videoUrl);

        return thumbnailUrl && embedUrl ? { thumbnailUrl, embedUrl } : null;
    }, [videoUrl]);

    const fetchPictures = useCallback(async () => {
        if (!fleetVehicleDetail.id) return;
        try {
            const res = await getFleetVehiclePicturesAPI(fleetVehicleDetail.id, initialDataWithPagination.pagination, "");
            setFetchedPictures(res.data);
        } catch {
            setFetchedPictures([]);
        }
    }, [fleetVehicleDetail.id]);

    useEffect(() => {
        fetchPictures();
    }, [fetchPictures]);

    useImperativeHandle(ref, () => ({
        refresh: fetchPictures,
    }));

    // Ako nema slika, koristimo dummy slike
    const dummyImages = [
        { id: "dummy-1", url: "/media/images/logo.png", name: "" },
        { id: "dummy-2", url: "/media/images/logo.png", name: "" },
        { id: "dummy-3", url: "/media/images/logo.png", name: "" },
        { id: "dummy-4", url: "/media/images/logo.png", name: "" },
        { id: "dummy-5", url: "/media/images/logo.png", name: "" },
        { id: "dummy-6", url: "/media/images/logo.png", name: "" },
    ];

    const isDummy = fetchedPictures.length === 0;
    const pictures = !isDummy
        ? fetchedPictures.map(p => ({ id: p.id, url: p.url, name: p.name ?? "" }))
        : dummyImages;

    const handlePictureClick = (pictureId: string) => {
        if (isDummy) return;
        setSelectedPictureId(pictureId);
        setPreviewOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
        setSelectedPictureId(null);
    };

    const handleDelete = async (ids: string[]) => {
        await Promise.all(ids.map((id) => deleteVehiclePictureAPI(id, t, true)));
        showNotification(t("admin.fleet.vehicle.picture.message.delete"), {
            variant: "success",
        });
        await fetchPictures();
        onPictureDeleted?.();
    };

    const handleReorder = async (orderedIds: string[]) => {
        if (!fleetVehicleDetail.id) return;
        await reorderVehiclePicturesAPI(fleetVehicleDetail.id, orderedIds, t, true);
        showNotification(t("admin.fleet.vehicle.picture.reorder.message.success"), {
            variant: "success",
        });
        await fetchPictures();
        onPictureDeleted?.();
    };

    // Video je prva pločica, ali ne ulazi u `pictures` — preglednik slika bi ga inače
    // nudio za brisanje i presoravanje.
    const tiles: { id: string; url: string; name: string; isVideo?: boolean }[] = video
        ? [{ id: "video", url: video.thumbnailUrl, name: "Video", isVideo: true }, ...pictures]
        : pictures;

    const visiblePictures = tiles.slice(0, 5);
    const remainingCount = tiles.length - 5;

    // Layout: [Large][Large][Small/Small stacked][Large with overlay]
    // Grid: 4 columns, 2 rows
    // Index 0: col 1, row-span-2 (large)
    // Index 1: col 2, row-span-2 (large)
    // Index 2: col 3, row 1 (small)
    // Index 3: col 3, row 2 (small)
    // Index 4: col 4, row-span-2 (large with overlay)

    return (
        <>
            <div
                className="pt-4 px-4 grid gap-2 rounded-lg overflow-hidden mb-3"
                style={{
                    gridTemplateColumns: visiblePictures.length === 1
                        ? "1fr"
                        : visiblePictures.length === 2
                            ? "1fr 1fr"
                            : visiblePictures.length === 3
                                ? "1fr 1fr 1fr"
                                : visiblePictures.length === 4
                                    ? "2fr 2fr 1fr"
                                    : "2fr 2fr 1fr 2fr",
                    gridTemplateRows: visiblePictures.length <= 3
                        ? "280px"
                        : "140px 140px",
                }}
            >
                {visiblePictures.map((picture, index) => {
                    const total = visiblePictures.length;
                    const isLastVisible = index === total - 1;
                    const showOverlay = isLastVisible && remainingCount > 0;

                    const getRadiusClasses = () => {
                        if (total === 1) return "rounded-lg";
                        if (total === 2) {
                            if (index === 0) return "rounded-l-lg";
                            if (index === 1) return "rounded-r-lg";
                        }
                        if (total === 3) {
                            if (index === 0) return "rounded-l-lg";
                            if (index === 2) return "rounded-r-lg";
                        }
                        if (total === 4) {
                            if (index === 0) return "rounded-l-lg";
                            if (index === 2) return "rounded-tr-lg";
                            if (index === 3) return "rounded-br-lg";
                        }
                        if (total >= 5) {
                            if (index === 0) return "rounded-l-lg";
                            if (index === 4) return "rounded-r-lg";
                        }
                        return "";
                    };

                    const getGridPosition = (): React.CSSProperties => {
                        if (total >= 5) {
                            // Explicit positioning for 5 images
                            switch (index) {
                                case 0: return { gridColumn: "1", gridRow: "1 / 3" }; // Large, col 1
                                case 1: return { gridColumn: "2", gridRow: "1 / 3" }; // Large, col 2
                                case 2: return { gridColumn: "3", gridRow: "1" };     // Small, col 3 top
                                case 3: return { gridColumn: "3", gridRow: "2" };     // Small, col 3 bottom
                                case 4: return { gridColumn: "4", gridRow: "1 / 3" }; // Large, col 4
                            }
                        }
                        if (total === 4) {
                            switch (index) {
                                case 0: return { gridColumn: "1", gridRow: "1 / 3" };
                                case 1: return { gridColumn: "2", gridRow: "1 / 3" };
                                case 2: return { gridColumn: "3", gridRow: "1" };
                                case 3: return { gridColumn: "3", gridRow: "2" };
                            }
                        }
                        return {};
                    };

                    return (
                        <div
                            key={picture.id || index}
                            className={`relative overflow-hidden bg-gray-100 ${!isDummy || picture.isVideo ? "cursor-pointer" : ""} ${getRadiusClasses()}`}
                            style={getGridPosition()}
                            onClick={() => (picture.isVideo ? setVideoOpen(true) : handlePictureClick(picture.id))}
                        >
                            <img
                                src={picture.url}
                                alt={picture.name || `Slika ${index + 1}`}
                                className="absolute inset-0 w-full h-full object-cover"
                            />

                            {/* Pločica videa: zatamnjenje i play gumb, da se odmah razlikuje
                                od fotografija, a ostane dio iste mreže. */}
                            {picture.isVideo && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                    <span className="flex items-center justify-center w-12 h-12 rounded-full bg-white/90 shadow-lg">
                                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-slate-800 ml-0.5">
                                            <path d="M8 5v14l11-7z" />
                                        </svg>
                                    </span>
                                </div>
                            )}

                            {showOverlay && !isDummy && (
                                <div className="absolute inset-0 flex items-end justify-end p-3">
                                    <span className="text-white text-sm font-medium bg-black/20 backdrop-blur-sm px-3 py-1.5 rounded-md">
                                        {t("admin.fleet.vehicle.detail.gallery.showallphotos")} ({pictures.length})
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <PictureGalleryPreview
                show={previewOpen}
                pictures={pictures}
                initialSelectedId={selectedPictureId}
                onClose={handleClosePreview}
                allowDelete={!isDummy}
                onDelete={handleDelete}
                allowReorder={!isDummy}
                onReorder={handleReorder}
                deleteDialogTitle={t("admin.fleet.vehicle.edit.picture.dialog.delete.title")}
                deleteDialogMessage={t("admin.fleet.vehicle.edit.picture.dialog.delete.message")}
                deleteDialogConfirmText={t("admin.fleet.vehicle.edit.picture.dialog.delete.confirmtext")}
                deleteDialogCancelText={t("admin.fleet.vehicle.edit.picture.dialog.delete.canceltext")}
            />

            {video && (
                <Modal show={videoOpen} onHide={() => setVideoOpen(false)} size="large" position="centered">
                    <ModalHeader title="Video" onHide={() => setVideoOpen(false)} />
                    <ModalBody>
                        <div className="aspect-video w-full">
                            <iframe
                                src={video.embedUrl}
                                title="YouTube video"
                                className="w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </div>
                    </ModalBody>
                </Modal>
            )}
        </>
    );
});

export default VehicleGalleryPreview;
